import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ButtonBase,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { toast } from '../../../../../ui/toast';
import { statusPatch } from '../../../../../utils/api_service/JobOpenings';
import { PatchProgress } from '../../../../../utils/types/JobOpenings';

type SubStage = {
  label: string;
  status: string;
};

type Stage = {
  label: string;
  status: string;
  subStages: SubStage[];
  currentSubStageIndex: number;
};

type ProgressTrackerProps = {
  title: string;
  globalState: string;
  stages: Stage[];
  onClose: () => void;
  show: boolean;
  setShow: (value: boolean | ((prev: boolean) => boolean)) => void;
  jobOpeningId: string;
  setStages: (value: any) => void;
};

const statusDotColors: Record<string, string> = {
  Completed: '#10b981',
  'In Progress': '#3b82f6',
  Inactive: '#9ca3af',
  Pending: '#f59e0b',
  Closed: '#475569',
  'Decision in Progress': '#6366f1',
  'Offer in Progress': '#a855f7',
  'Offer Accepted': '#059669',
  'No Suitable Candidate': '#ef4444',
  'No Offers Accepted': '#b91c1c',
  'Job Request Withdrawn': '#f97316',
};

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  globalState,
  stages,
  jobOpeningId,
  setStages,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const PT = ADMIN_PAGES_KEYS.jobOpenings.progressTracker;

  const STAGE_LABEL_KEYS: Record<string, string> = {
    'Job Description': PT.labels.jobDescription,
    'Job Post': PT.labels.jobPost,
    'Shortlisting': PT.labels.shortlisting,
    'Interviews': PT.labels.interviews,
    'Decision Making': PT.labels.decisionMaking,
    'Closed': PT.labels.closed,
  };

  const STATUS_KEYS: Record<string, string> = {
    'Inactive': PT.statuses.inactive,
    'Pending': PT.statuses.pending,
    'In Progress': PT.statuses.inProgress,
    'Completed': PT.statuses.completed,
    'Decision in Progress': PT.statuses.decisionInProgress,
    'Offer in Progress': PT.statuses.offerInProgress,
    'Offer Accepted': PT.statuses.offerAccepted,
    'No Suitable Candidate': PT.statuses.noSuitableCandidate,
    'No Offers Accepted': PT.statuses.noOffersAccepted,
    'Job Request Withdrawn': PT.statuses.jobRequestWithdrawn,
  };

  const [openStageIndex, setOpenStageIndex] = useState<number | null>(null);

  const overallProgress = useMemo(() => {
    const completedStages = stages.filter(
      (stage) => stage.status === 'Completed' || stage.status === 'Offer Accepted',
    ).length;
    return (completedStages / stages.length) * 100;
  }, [stages]);

  const toTitleCase = (str: string) => {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const mapDecisionMakingStatus = (status: string): string => {
    const statusMappings: Record<string, string> = {
      'In Progress': 'Decision in Progress',
      'Decision in Progress': 'Decision in Progress',
      'Offer in Progress': 'Offer in Progress',
      Inactive: 'Inactive',
      Completed: 'Completed',
    };
    return statusMappings[status] || status;
  };

  const mapShortlistingStatus = (status: string): string => {
    const statusMappings: Record<string, string> = {
      Pending: 'Inactive',
      'In Progress': 'In Progress',
      Inactive: 'Inactive',
      Closed: 'Closed',
      Completed: 'Completed',
    };
    return statusMappings[status] || status;
  };

  const mapJobPostStatus = (status: string): string => {
    const statusMappings: Record<string, string> = {
      Pending: 'Inactive',
      'In Progress': 'In Progress',
      Inactive: 'Inactive',
      Published: 'Published',
      Completed: 'Completed',
    };
    return statusMappings[status] || status;
  };

  const mapGeneralStatus = (status: string): string => {
    const statusMappings: Record<string, string> = {
      Pending: 'Inactive',
      'In Progress': 'In Progress',
      Inactive: 'Inactive',
      Completed: 'Completed',
    };
    return statusMappings[status] || status;
  };

  const clickableStages = [
    'Shortlisting',
    'Decision Making',
    'Closed',
    'Job Post',
    'Interviews',
  ];

  const updateStage = async (
    stageClicked: string,
    newStage: string,
  ) => {
    const hasShortlisting = stages.some(
      (stage) => stage.label === 'Shortlisting',
    );
    const offset = hasShortlisting ? -1 : 0;

    const statesObject: PatchProgress = {
      jobDescription: mapGeneralStatus(toTitleCase(stages[0].status)),
      jobPost:
        newStage === 'Job Post'
          ? mapJobPostStatus(toTitleCase(stageClicked))
          : mapJobPostStatus(toTitleCase(stages[1].status)),
      shortlisting:
        newStage === 'Shortlisting'
          ? mapShortlistingStatus(toTitleCase(stageClicked))
          : mapShortlistingStatus(toTitleCase(stages[2 + offset].status)),
      interviews:
        newStage === 'Interviews'
          ? mapGeneralStatus(toTitleCase(stageClicked))
          : mapGeneralStatus(toTitleCase(stages[3 + offset].status)),
      decisionMaking:
        newStage === 'Decision Making'
          ? mapDecisionMakingStatus(stageClicked)
          : mapDecisionMakingStatus(stages[4 + offset].status),
      closed:
        newStage === 'Closed'
          ? toTitleCase(stageClicked)
          : toTitleCase(stages[5 + offset].status),
    };

    switch (newStage) {
      case 'Job Description':
        statesObject.jobDescription = mapGeneralStatus(toTitleCase(stageClicked));
        break;
      case 'Job Post':
        statesObject.jobPost = mapJobPostStatus(toTitleCase(stageClicked));
        break;
      case 'Shortlisting':
        statesObject.shortlisting = mapShortlistingStatus(toTitleCase(stageClicked));
        break;
      case 'Interviews':
        statesObject.interviews = mapGeneralStatus(toTitleCase(stageClicked));
        break;
      case 'Decision Making':
        statesObject.decisionMaking = mapDecisionMakingStatus(stageClicked);
        break;
      case 'Closed':
        statesObject.closed = toTitleCase(stageClicked);
        break;
    }

    setStages((prevStages: any[]) =>
      prevStages.map((stage) => {
        if (stage.label === newStage) {
          const newCurrentSubStageIndex = stage.subStages.findIndex(
            (sub: any) => sub.status === stageClicked,
          );

          return {
            ...stage,
            status: stageClicked,
            currentSubStageIndex:
              newCurrentSubStageIndex !== -1
                ? newCurrentSubStageIndex
                : stage.currentSubStageIndex,
          };
        }
        return stage;
      }),
    );

    try {
      await statusPatch(jobOpeningId, statesObject as PatchProgress);

      if (newStage === 'Closed' && stageClicked === 'Offer Accepted') {
        toast.success(
          'Success! Job opening has been moved to closed - Offer Accepted',
          { position: 'bottom-center', autoClose: 4000 },
        );
      } else if (
        newStage === 'Decision Making' &&
        stageClicked === 'Decision in Progress'
      ) {
        toast.success('Status updated to Decision in Progress', {
          position: 'bottom-center',
        });
      } else if (
        newStage === 'Decision Making' &&
        stageClicked === 'Offer in Progress'
      ) {
        toast.success('Status updated to Offer in Progress', {
          position: 'bottom-center',
        });
      } else if (stageClicked === 'Completed') {
        toast.success(`${newStage} completed successfully!`, {
          position: 'bottom-center',
        });
      } else {
        toast.success(`Status updated to ${stageClicked}`, {
          position: 'bottom-center',
        });
      }
    } catch {
      toast.error('Failed to change state', { position: 'bottom-center' });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        p: 1,
        overflow: 'visible',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ lg: 'center' }}
        spacing={1}
      >
        {/* Global State Badge */}
        <Paper
          elevation={0}
          sx={{
            minHeight: 36,
            px: 1.5,
            py: 0.5,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            display: 'flex',
            flexDirection: { xs: 'row', lg: 'column' },
            alignItems: { xs: 'center', lg: 'flex-start' },
            justifyContent: { xs: 'space-between', lg: 'center' },
            minWidth: { lg: 90 },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.55rem',
              fontWeight: 800,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              lineHeight: 1,
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.ui.stateLabel)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: 'primary.main',
              lineHeight: 1.2,
            }}
            noWrap
          >
            {globalState}
          </Typography>
        </Paper>

        {/* Stages */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            flex: 1,
            overflowX: 'auto',
            pb: { xs: 0.5, lg: 0 },
            flexWrap: { lg: 'wrap' },
          }}
        >
          {stages.map((stage, index) => {
            const isOpen = openStageIndex === index;
            const clickable = clickableStages.includes(stage.label);
            const isCompleted =
              stage.status === 'Completed' || stage.status === 'Offer Accepted';
            const isInProgress = stage.status === 'In Progress';

            return (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  minWidth: { xs: 130, lg: 90 },
                  flex: 1,
                }}
              >
                {/* Stage Button Card */}
                <ButtonBase
                  onClick={() => setOpenStageIndex(isOpen ? null : index)}
                  sx={{
                    width: '100%',
                    minHeight: { xs: 40, lg: 32 },
                    p: 0.75,
                    px: 1,
                    border: 1,
                    borderColor: statusDotColors[stage.status] || 'divider',
                    borderRadius: 1.5,
                    bgcolor: isCompleted
                      ? 'success.50'
                      : isInProgress
                        ? 'primary.50'
                        : 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 0.5,
                    textAlign: 'left',
                    boxShadow: isOpen ? 1 : 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 1,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1.1,
                        display: 'block',
                      }}
                      noWrap
                    >
                      {t(STAGE_LABEL_KEYS[stage.label] ?? stage.label)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: statusDotColors[stage.status] || 'grey.400',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.6rem',
                          color: 'text.secondary',
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                        noWrap
                      >
                        {t(STATUS_KEYS[stage.status] ?? stage.status)}
                      </Typography>
                    </Stack>
                  </Box>

                  {stage.subStages.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                        color: 'text.secondary',
                      }}
                    >
                      <ChevronDown size={12} />
                    </Box>
                  )}
                </ButtonBase>

                {/* Dropdown Sub-stages */}
                {isOpen && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '100%',
                      zIndex: 9999,
                      mt: 0.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'primary.light',
                      bgcolor: 'background.paper',
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}
                  >
                    {stage.subStages.map((sub, subIndex) => {
                      const isCurrent = stage.currentSubStageIndex === subIndex;
                      const isSubCompleted = subIndex < stage.currentSubStageIndex;

                      return (
                        <Box
                          key={subIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (clickable) {
                              updateStage(
                                stage.subStages[subIndex].status,
                                stage.label,
                              );
                              setOpenStageIndex(null);
                            }
                          }}
                          sx={{
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: 1,
                            borderColor: 'divider',
                            bgcolor: isCurrent
                              ? 'primary.50'
                              : isSubCompleted
                                ? 'success.50'
                                : 'background.paper',
                            color: isCurrent
                              ? 'primary.dark'
                              : isSubCompleted
                                ? 'success.dark'
                                : 'text.secondary',
                            cursor: clickable ? 'pointer' : 'default',
                            transition: 'background-color 0.15s',
                            '&:hover': {
                              bgcolor: clickable ? 'action.hover' : 'none',
                            },
                            '&:last-child': {
                              borderBottom: 0,
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                            noWrap
                          >
                            {t(STATUS_KEYS[sub.label] ?? sub.label)}
                          </Typography>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: statusDotColors[sub.status] || 'grey.400',
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Paper>
                )}
              </Box>
            );
          })}
        </Stack>
      </Stack>

      {/* Bottom Progress Bar */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <LinearProgress
          variant="determinate"
          value={overallProgress}
          sx={{
            height: 3,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              transition: 'transform 0.5s ease',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ProgressTracker;
